import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import dayjs from 'dayjs';

import DateDrawer from './DateDrawer';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import DropdownMenu from '../ui/DropdownMenu';
import { Complex, Field, MenuItem } from '../../types';
import Spinner from '../loading/Spinner';
import InputField from '../forms/form-elements/InputField';
import Button from '../buttons/Button';
import { toast } from 'react-toastify';
import Divider from '../ui/Divider';

type Props = {
  openFilters: boolean;
  closeFilters: () => void;
  complexMenuItems: MenuItem<Complex | null>[];
  selectedComplex: MenuItem<Complex> | null;
  fieldMenuItems: Array<MenuItem<Field | null>>;
  selectedField: MenuItem<Field> | null;
  loadingFields: boolean;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  initialCaptain: string;
  initialOwner: string;
  initialPhone: string;
  initialTeam: string;
  fetchFields: (complexId: number) => Promise<void>;
  onApplyFilters: (
    complex: MenuItem<Complex> | null,
    field: MenuItem<Field> | null,
    startDate: Date | null,
    endDate: Date | null,
    captain: string,
    owner: string,
    phone: string,
    team: string
  ) => Promise<void>;
};

const ReservationFiltersModal = ({
  openFilters,
  closeFilters,
  complexMenuItems,
  selectedComplex,
  fieldMenuItems,
  selectedField,
  loadingFields,
  selectedStartDate,
  selectedEndDate,
  onApplyFilters,
  fetchFields,
  initialCaptain,
  initialOwner,
  initialPhone,
  initialTeam,
}: Props) => {
  const [startDateDrawerOpen, setStartDateDrawerOpen] = useState(false);
  const [endDateDrawerOpen, setEndDateDrawerOpen] = useState(false);

  const [complex, setComplex] = useState<MenuItem<Complex> | null>(null);
  const [field, setField] = useState<MenuItem<Field> | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [captain, setCaptain] = useState('');
  const [owner, setOwner] = useState('');
  const [phone, setPhone] = useState('');
  const [team, setTeam] = useState('');

  const { t } = useTranslation();

  const isDisabled = useMemo(() => {
    if (!complex) {
      return true;
    }
    if (startDate && endDate && startDate > endDate) {
      return true;
    }
    return false;
  }, [complex, startDate, endDate]);

  useEffect(() => {
    setComplex(selectedComplex);
    setField(selectedField);
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
    setCaptain(initialCaptain);
    setOwner(initialOwner);
    setPhone(initialPhone);
    setTeam(initialTeam);
  }, [
    initialCaptain,
    initialOwner,
    initialPhone,
    initialTeam,
    selectedComplex,
    selectedEndDate,
    selectedField,
    selectedStartDate,
  ]);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      toast.error(t(COMMON_TRANSLATE_KEYS.START_DATE_AFTER_END_DATE_ERROR));
    }
  }, [startDate, endDate, t]);

  return (
    <Dialog
      open={openFilters}
      onClose={closeFilters}
      className="fixed inset-0 flex items-center justify-center z-[50]"
      static={startDateDrawerOpen || endDateDrawerOpen}
    >
      <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />
      <div className="lg:bg-secondaryBlack bg-additionalBlack lg:w-[65%] w-[60%] h-[80%] overflow-auto p-6 rounded-md absolute top-212 left-468">
        <Dialog.Title>
          <div className="flex flex-col">
            <div className="flex w-full justify-end text-mainWhite">
              <button onClick={() => closeFilters()}>X</button>
            </div>
            <h1 className="flex flex-row justify-start text-white text-xl font-bold mb-6 text-start">
              {t(COMMON_TRANSLATE_KEYS.FILTERS)}
            </h1>
          </div>
        </Dialog.Title>

        <div className="flex flex-col w-full gap-5 h-full bg-secondaryBlack rounded-xl p-5">
          <div className="flex flex-col w-full">
            <div className="flex flex-col">
              <span className="text-mainBlue">
                {`${t(COMMON_TRANSLATE_KEYS.COMPLEX)}`}
                <span className="text-functionalRed">*</span>
              </span>
              <DropdownMenu<Complex | null>
                items={complexMenuItems}
                selectedOption={complex}
                setSelectedOption={(newComplex: MenuItem<Complex> | null) => {
                  setComplex(newComplex);
                  setField(null);
                  if (newComplex) {
                    fetchFields(newComplex?.value.id);
                  }
                }}
                showIcon={false}
                placeholder={t(
                  COMMON_TRANSLATE_KEYS.COMPLEX_SELECT_PLACEHOLDER
                )}
                menuOutline
              />
            </div>
          </div>

          <div className="flex flex-col w-full">
            <div className="flex flex-col">
              <span className="text-mainBlue">
                {`${t(COMMON_TRANSLATE_KEYS.FIELD)}`}
              </span>
              <DropdownMenu<Field | null>
                items={fieldMenuItems}
                selectedOption={field}
                setSelectedOption={setField}
                showIcon={false}
                placeholder={t(COMMON_TRANSLATE_KEYS.FIELD_SELECT_PLACEHOLDER)}
                iconPlaceholder={
                  loadingFields ? (
                    <Spinner size={15} color="gray" loading />
                  ) : undefined
                }
                menuOutline
              />
            </div>
          </div>

          <div className="lg:grid lg:grid-cols-2 flex flex-col gap-5">
            <div>
              <p className="text-mainBlue">
                {t(COMMON_TRANSLATE_KEYS.PERIOD_START)}
              </p>
              <InputField
                placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}
                onClick={() => setStartDateDrawerOpen(true)}
                readOnly
                value={startDate ? dayjs(startDate).format('DD.MM.YYYY') : ''}
                color="text-mainBlue"
                className="hover:cursor-pointer"
              />
            </div>
            <div>
              <p className="text-mainBlue">
                {t(COMMON_TRANSLATE_KEYS.PERIOD_END)}
              </p>
              <InputField
                placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}
                onClick={() => setEndDateDrawerOpen(true)}
                readOnly
                value={endDate ? dayjs(endDate).format('DD.MM.YYYY') : ''}
                color="text-mainBlue"
                className="hover:cursor-pointer"
              />
            </div>
          </div>

          <div className="lg:grid lg:grid-cols-2 flex flex-col gap-5">
            <div className="flex flex-col w-full gap-1">
              <p className="text-mainBlue">
                {t(COMMON_TRANSLATE_KEYS.CAPTAIN)}
              </p>
              <InputField
                onChange={(e) => setCaptain(e.target.value)}
                value={captain}
                placeholder={t(COMMON_TRANSLATE_KEYS.CAPTAIN_NAME)}
              />
            </div>

            <div className="flex flex-col w-full gap-1">
              <p className="text-mainBlue">{t(COMMON_TRANSLATE_KEYS.OWNER)}</p>
              <InputField
                onChange={(e) => setOwner(e.target.value)}
                value={owner}
                placeholder={t(COMMON_TRANSLATE_KEYS.RESERVATION_OWNER_NAME)}
              />
            </div>

            <div className="flex flex-col w-full gap-1">
              <p className="text-mainBlue">{t(COMMON_TRANSLATE_KEYS.PHONE)}</p>
              <InputField
                onChange={(e) => setPhone(e.target.value)}
                value={phone}
                placeholder={t(COMMON_TRANSLATE_KEYS.OWNER_PHONE_PLACEHOLDER)}
              />
            </div>

            <div className="flex flex-col w-full gap-1">
              <p className="text-mainBlue">{t(COMMON_TRANSLATE_KEYS.TEAM)}</p>
              <InputField
                onChange={(e) => setTeam(e.target.value)}
                value={team}
                placeholder={t(COMMON_TRANSLATE_KEYS.TEAM_NAME)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-8 justify-center items-center p-1">
            <Divider mt={5} />
            <Button
              onClick={() =>
                onApplyFilters(
                  complex,
                  field,
                  startDate,
                  endDate,
                  captain,
                  owner,
                  phone,
                  team
                )
              }
              type="button"
              backgroundColor="bg-functionalGreen"
              textColor="text-white"
              width={16}
              height={3}
              isDisabled={isDisabled}
            >
              {t(COMMON_TRANSLATE_KEYS.APPLY)}
            </Button>
          </div>
        </div>
      </div>

      <DateDrawer
        isOpen={startDateDrawerOpen}
        closeDrawer={() => setStartDateDrawerOpen(false)}
        selectedDate={startDate || null}
        onSelectDate={(date) => {
          setStartDate(date);
          setStartDateDrawerOpen(false);
        }}
        title={t(COMMON_TRANSLATE_KEYS.PERIOD_START)}
        onMonthDateChange={async () => {}}
      />

      <DateDrawer
        isOpen={endDateDrawerOpen}
        closeDrawer={() => setEndDateDrawerOpen(false)}
        selectedDate={endDate || null}
        onSelectDate={(date) => {
          setEndDate(date);
          setEndDateDrawerOpen(false);
        }}
        title={t(COMMON_TRANSLATE_KEYS.PERIOD_END)}
        onMonthDateChange={async () => {}}
      />
    </Dialog>
  );
};

export default ReservationFiltersModal;
